/**
 * Subset of the full `Plan` interface defined in `components/PricingContent.tsx`.
 * Includes only the fields `getButtonState` needs. Structural typing means
 * the richer `Plan` is assignable to this. If either drifts, consider
 * promoting to a shared types module.
 */
export interface Plan {
  id: string;
  name: string;
  price: number;
}

export interface ButtonStateContext {
  loading: boolean;
  isAuthenticated: boolean;
  userSubscription: { plan_id: string; plan_name?: string } | null;
}

export interface ButtonState {
  disabled: boolean;
  label: string;
}

const PRO_AGENCY_KEYWORDS = ["pro", "agency"];
const STANDARD_KEYWORDS = ["standard"];

const isProOrAgencyPlan = (planName: string): boolean =>
  PRO_AGENCY_KEYWORDS.some((k) => planName.toLowerCase().includes(k));

const isStandardPlan = (planName?: string): boolean => {
  if (!planName) return false;
  return STANDARD_KEYWORDS.some((k) => planName.toLowerCase().includes(k));
};

export function getButtonState(
  plan: Plan,
  ctx: ButtonStateContext,
  allPlans: Plan[],
): ButtonState {
  const isFree = plan.price === 0;
  if (ctx.loading) return { disabled: true, label: "Loading..." };

  const sub = ctx.userSubscription;

  // Free plan: only show "Current Plan" if user has no active paid subscription
  if (isFree && ctx.isAuthenticated) {
    const hasPaidSubscription = sub !== null;
    if (!hasPaidSubscription) return { disabled: true, label: "Current Plan" };
    // User has a paid subscription (Standard or Pro/Agency) - disable Free plan
    return { disabled: true, label: "Choose Plan" };
  }
  const userHasProOrAgency =
    sub !== null && isProOrAgencyPlan(allPlans.find((p) => p.id === sub.plan_id)?.name || "");

  if (userHasProOrAgency) return { disabled: true, label: "Current Plan" };

  if (
    ctx.userSubscription &&
    isStandardPlan(ctx.userSubscription.plan_name) &&
    isProOrAgencyPlan(plan.name)
  ) {
    return { disabled: false, label: "Upgrade to Pro" };
  }

  if (ctx.userSubscription && isStandardPlan(plan.name)) {
    return { disabled: true, label: "Current Plan" };
  }

  return { disabled: false, label: "Choose plan" };
}
